import "react-app-polyfill/stable";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import "chart.js/auto";

import App from "./App";
import reportWebVitals from "./utils/reportWebVitals";
import { ColorModeProvider } from "./contexts/ColorModeContext";
import {
  AuthenticationResult,
  EventType,
  PublicClientApplication,
} from "@azure/msal-browser";
import { msalConfig } from "./authConfig";

// Non-production builds (REACT_APP_ENV_LABEL set) get the orange favicon so their
// browser tabs are easy to tell apart from production.
if (process.env.REACT_APP_ENV_LABEL) {
  const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (icon) icon.href = `${process.env.PUBLIC_URL}/favicon-nonprod.svg`;
}

const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL instance first
const initializeAndRenderApp = async () => {
  try {
    // Initialize MSAL
    await msalInstance.initialize();

    // Handle redirect promise after initialization
    const response = await msalInstance.handleRedirectPromise();

    if (response) {
      msalInstance.setActiveAccount(response.account);
    } else {
      // If no redirect response, check if there are accounts
      const accounts = msalInstance.getAllAccounts();
      if (!msalInstance.getActiveAccount() && accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }

    // Add event callback after initialization
    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        if (payload.account) {
          msalInstance.setActiveAccount(payload.account);
        }
      }
    });

    // Render React app after everything is initialized
    const container = document.getElementById("root");
    if (container) {
      const root = createRoot(container);
      root.render(
        <BrowserRouter>
          <ColorModeProvider>
            <App instance={msalInstance} />
          </ColorModeProvider>
        </BrowserRouter>
      );
    }
  } catch (error) {
    console.error("Error initializing MSAL:", error);

    // Still render the app even if there's an error, but with basic setup
    const container = document.getElementById("root");
    if (container) {
      const root = createRoot(container);
      root.render(
        <BrowserRouter>
          <ColorModeProvider>
            <App instance={msalInstance} />
          </ColorModeProvider>
        </BrowserRouter>
      );
    }
  }
};

// Start the initialization process
initializeAndRenderApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
